import React from 'react';
import './Profile.scss';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { useApiFetch } from '../../api/fetcher';
import { ProfileType } from '../../types/profileTypes';
import { PROFILE_URL, PASSWORD_CHANGE } from '../../api/urls';
import { FloatLabel } from 'primereact/floatlabel';

const Profile: React.FC = () => {
    const apiFetch = useApiFetch();

    const [profile, setProfile] = React.useState<ProfileType | null>(null);
    const [loadingProfile, setLoadingProfile] = React.useState<boolean>(true);

    const [currentPassword, setCurrentPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [changeLoading, setChangeLoading] = React.useState(false);
    const [changeError, setChangeError] = React.useState<string | null>(null);
    const [changeSuccess, setChangeSuccess] = React.useState<string | null>(null);

    React.useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const res = await apiFetch(PROFILE_URL, { credentials: 'include' });
                const json = await res.json();
                if (!isMounted) return;
                setProfile(json as ProfileType);
            } catch (e) {
                if (!isMounted) return;
                setProfile(null);
            } finally {
                if (isMounted) setLoadingProfile(false);
            }
        })();
        return () => { isMounted = false; };
    }, [apiFetch]);

    const formatDateTime = (iso?: string) => (iso ? new Date(iso).toLocaleString('pl-PL') : '-');

    const onPasswordChangeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setChangeError(null);
        setChangeSuccess(null);

        if (newPassword !== confirmPassword) {
            setChangeError('Nowe hasła muszą się zgadzać.');
            return;
        }
        if (newPassword.length < 8) {
            setChangeError('Nowe hasło musi mieć co najmniej 8 znaków.');
            return;
        }

        setChangeLoading(true);
        try {
            const res = await apiFetch(PASSWORD_CHANGE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Nie udało się zmienić hasła');
            }
            setChangeSuccess('Hasło zostało zmienione.');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err: any) {
            setChangeError(err?.message || 'Wystąpił błąd podczas zmiany hasła');
        } finally {
            setChangeLoading(false);
        }
    };

    return (
        <div className='card-margins panel-margin-top'>
            <Card
                title="Profil użytkownika"
                subTitle="Zarządzaj swoimi danymi i ustawieniami konta"
                className='card-border'>
                <Card className='mb-4' style={{ borderRadius: '14px' }}>
                    <h6>Informacje kontaktowe</h6>
                    <div className='mt-4'>
                        <p className='mb-0'>Data dodania:</p>
                        <p style={{ fontWeight: 500 }}>{loadingProfile ? '-' : formatDateTime(profile?.created)}</p>
                    </div>
                    <Divider></Divider>
                    <div className='mt-4'>
                        <p className='mb-0'>Email:</p>
                        <p style={{ fontWeight: 500 }}>{loadingProfile ? '-' : (profile?.username ?? '-')}</p>
                    </div>
                    <div className='mt-4'>
                        <p className='mb-0'>Firma:</p>
                        <p style={{ fontWeight: 500 }}>{loadingProfile ? '-' : (profile?.account_name ?? '-')}</p>
                    </div>
                    <div className='mt-4'>
                        <p className='mb-0'>Rola:</p>
                        <p className='mb-0' style={{ fontWeight: 500 }}>{loadingProfile ? '-' : (profile?.is_admin ? 'Administrator' : 'Użytkownik')}</p>
                    </div>
                </Card>

                <Card style={{ borderRadius: '14px' }}>
                    <h6 className='mb-4'>Zmiana hasła</h6>
                    <form onSubmit={onPasswordChangeSubmit} className='mt-3 d-flex column gap-3 password-input'>
                        <div className='d-flex row'>
                            <span className="p-float-label mb-4">
                                <FloatLabel>
                                    <Password id="currentPassword" inputId="currentPassword" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} toggleMask feedback={false} className='w-100' inputStyle={{ width: '100%' }} />
                                    <label htmlFor="currentPassword">Obecne hasło</label>
                                </FloatLabel>
                            </span>
                            <span className="p-float-label mb-4">
                                <FloatLabel>
                                    <Password id="newPassword" inputId="newPassword" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} toggleMask feedback={false} className='w-100' inputStyle={{ width: '100%' }} />
                                    <label htmlFor="newPassword">Nowe hasło</label>
                                </FloatLabel>
                            </span>
                            <span className="p-float-label">
                                <FloatLabel>
                                    <Password id="confirmPassword" inputId="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} toggleMask feedback={false} className='w-100' inputStyle={{ width: '100%' }} />
                                    <label htmlFor="confirmPassword">Potwierdź nowe hasło</label>
                                </FloatLabel>
                            </span>
                            <div style={{ minHeight: '24px', marginTop: '8px', marginBottom: '0' }}>
                                {changeError && <div className='text-danger' role='alert'>{changeError}</div>}
                                {changeSuccess && <div className='text-success' role='status'>{changeSuccess}</div>}
                            </div>
                            <div className='d-flex justify-content-start pt-2'>
                                <Button type='submit' className='save-button' label={changeLoading ? 'Zapisywanie…' : 'Zmień hasło'} loading={changeLoading} disabled={!currentPassword || !newPassword || !confirmPassword} />
                            </div>
                        </div>
                    </form>
                </Card>
            </Card>
        </div>
    );
};

export default Profile;